import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import logo from "@/assets/logo.svg";
import { cn } from "@/utils/cn";
import { motion, AnimatePresence } from "framer-motion";
import { isHomePage } from "@/utils/location";
import { useTranslation } from "react-i18next";
import { NAV_LINKS } from "@/constants/navigation";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const isHome = isHomePage(pathname);

  const languages = [
    { code: "hy", label: "Հայ", flag: "https://flagcdn.com/am.svg" },
    { code: "en", label: "Eng", flag: "https://flagcdn.com/gb.svg" },
    // { code: "ru", label: "Рус" },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const handleContactClick = () => {
    setIsMenuOpen(false); // Close mobile menu if open
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 w-full h-21.5 px-4 nav:px-10 xl:px-20 flex items-center justify-center z-100",
        isScrolled || isMenuOpen || !isHome
          ? "bg-background-1 shadow-md"
          : "bg-[#FFFFFF14] backdrop-blur-xl",
      )}
    >
      <div className="w-full h-full max-w-360 flex items-center justify-between">
        <Link to="/" className="w-62 h-11.25">
          <img src={logo} alt="Prof Project" className="w-full h-full" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden nav:flex max-w-204 h-10 items-center justify-between">
          <nav className="h-full py-2.5 flex items-center justify-center">
            <ul className="w-full h-5 flex items-center justify-center gap-4 xl:gap-5">
              {NAV_LINKS.map((link) => (
                <li key={link.to} className="h-full flex">
                  <Link
                    to={link.to}
                    className={cn(
                      "text-base text-[#2B2F33] hover:font-medium transition-colors whitespace-nowrap",
                      pathname === link.to && "font-bold"
                    )}
                  >
                    {t(link.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="relative ml-4" ref={langRef}>
            <div
              className="w-26.5 h-full px-5 py-2 flex items-center justify-center gap-2 cursor-pointer select-none"
              onClick={() => setIsLangOpen(!isLangOpen)}
            >
              <img src={currentLang.flag} alt={currentLang.label} className="w-6 h-4" />
              <p>{currentLang.label}</p>
              <motion.div
                animate={{ rotate: isLangOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown size={24} className="text-black" />
              </motion.div>
            </div>

            <AnimatePresence>
              {isLangOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 w-full bg-background-1 shadow-lg z-50"
                >
                  {languages.map((lang) => (
                    <div
                      key={lang.code}
                      className={cn(
                        "px-5 py-2 flex items-center gap-3 bg-white text-main-1 cursor-pointer transition-all duration-300 hover:brightness-95",
                        currentLang.code === lang.code &&
                          "bg-main-1 text-white font-medium",
                      )}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                    >
                      <img src={lang.flag} alt={lang.label} className="w-6 h-4" />
                      <span>{lang.label}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="h-full w-40.25 ml-4">
            <button 
              onClick={handleContactClick}
              className="w-full h-full py-2.5 px-5 bg-main-1 text-base text-background-1 cursor-pointer hover:bg-main-1/90 transition-colors"
            >
              {t("contact_us")}
            </button>
          </div>
        </div>

        {/* Burger Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="nav:hidden text-main-1 p-2 focus:outline-none"
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? <X size={32} /> : <Menu size={32} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "fixed top-21.5 left-0 w-full bg-background-1 transition-all duration-300 overflow-hidden nav:hidden border-t border-gray-100",
          isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <nav className="flex flex-col items-start justify-start h-full gap-8 py-10 px-5">
          <ul className="w-full flex flex-col items-start gap-6">
            {NAV_LINKS.map((link) => (
              <li key={link.to} className="w-full py-3 border-b border-main-1">
                <Link
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    "text-xl font-medium text-main-1 hover:text-[#2B2F33] transition-colors",
                    pathname === link.to && "font-bold"
                  )}
                >
                  {t(link.label)}
                </Link>
              </li>
            ))}
          </ul>

          <button
            onClick={handleContactClick}
            className="w-full py-4 px-6 bg-main-1 text-lg text-background-1 shadow-lg"
          >
            {t("contact_us")}
          </button>

          <div className="flex items-center gap-6">
            {languages.map((lang) => (
              <div
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={cn(
                  "flex items-center gap-2 cursor-pointer transition-all duration-300 py-2 px-3",
                  currentLang.code === lang.code 
                    ? "bg-main-1 text-white font-medium" 
                    : "text-main-1 hover:bg-background-2"
                )}
              >
                <img src={lang.flag} alt={lang.label} className="w-6 h-4" />
                <span className="text-base">{lang.label}</span>
              </div>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
